import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';

interface AuthGuardProps {
    children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
    const location = useLocation();

    useEffect(() => {
        const checkAuthAndRedirect = () => {
            const userCookie = Cookies.get('user');
            const tokenCookie = Cookies.get('token');
            // Read live, not from the render this timer was set up in - the
            // page can change (or cookies swap, e.g. starting an impersonation)
            // before this effect re-runs, and a stale path redirected wrongly.
            const currentPath = window.location.pathname;
            const isAdminPath = currentPath.startsWith('/admin');

            // Check if both cookies are present
            const isAuthenticated = userCookie && tokenCookie;


            if (isAuthenticated) {
                // role === 'master' is FineAnts' own platform-admin account -
                // it belongs to no company/entity, so it has nothing to do on
                // a company-scoped page, and conversely a company user (any
                // other role) must never reach the /admin/* console just by
                // typing the URL. Both previously only checked "is ANY valid
                // session present", not which kind, so either side could
                // reach the other's area at will.
                let isMasterUser = false;
                try {
                    isMasterUser = !!userCookie && JSON.parse(userCookie)?.role === 'master';
                } catch {
                    isMasterUser = false;
                }

                if (isAdminPath) {
                    if (!isMasterUser) {
                        // A company session has no business on any /admin/*
                        // page - bounce it to the admin login, same as an
                        // unauthenticated visitor would get.
                        if (currentPath !== '/admin') {
                            window.location.href = '/admin';
                        }
                    } else if (currentPath === '/admin') {
                        // Already an admin session - skip past the login page.
                        window.location.href = '/admin/company';
                    }
                } else {
                    if (isMasterUser) {
                        // The platform admin account has no company to view
                        // company-scoped pages for.
                        window.location.href = '/admin/company';
                    } else if (currentPath === '/login' || currentPath === '/') {
                        window.location.href = '/transaction';
                    }
                }
            } else {
                // Allow token-only access during company selection/creation
                const inCompanySelection = localStorage.getItem('inCompanySelection') === 'true';
                if (tokenCookie && inCompanySelection) {
                    // During company selection/creation, we have token but no user cookie yet
                    return;
                }

                if (userCookie || tokenCookie) {
                    Cookies.remove('user');
                    Cookies.remove('token');
                }

                if (isAdminPath && currentPath !== '/admin') {
                    window.location.href = '/admin';
                } else if (
                    !isAdminPath &&
                    !['/login', '/signup', '/forgot-password', '/reset-password', '/terms-and-conditions'].includes(currentPath) &&
                    !currentPath.startsWith('/view/')
                ) {
                    window.location.href = '/login';
                }
            }
        };

        checkAuthAndRedirect();

        const interval = setInterval(checkAuthAndRedirect, 1000);

        const handleStorageChange = () => {
            setTimeout(checkAuthAndRedirect, 100);
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [location.pathname]);

    return <>{children}</>;
};

export default AuthGuard;